import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { useHealth, useLogout, useMe } from "@/lib/api";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Guild";

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const backendStatus = health?.status ?? "unreachable";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border/70 bg-card/90 flex items-center justify-between border-b px-6 py-4 backdrop-blur">
        <div className="flex items-center gap-3">
          <img src="/brand/logo.png" alt="BOI logo" className="h-8 w-auto" />
          <div>
            <p className="font-heading text-primary text-lg font-bold">{PROJECT_NAME}</p>
            <p className="text-muted text-xs uppercase tracking-[0.24em]">BOI Talent OS</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/profile">
            <Button variant="ghost" size="sm">
              Profile
            </Button>
          </Link>
          {me?.role === "admin" && (
            <>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  Admin
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="ghost" size="sm">
                  Activity
                </Button>
              </Link>
            </>
          )}
          <Button variant="outline" onClick={handleLogout}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-12">
        <div className="space-y-10">
          <section className="from-primary to-primary/90 rounded-3xl bg-gradient-to-br px-8 py-10 text-white shadow-xl">
            <p className="text-accent-light text-sm font-medium uppercase tracking-[0.28em]">
              Board of Innovation
            </p>
            <h2 className="font-heading mt-4 text-4xl font-bold">
              Coordinate elite freelance talent with the speed of an AI-native team.
            </h2>
            <p className="mt-4 max-w-2xl text-sm text-white/78">
              Guild helps BOI assign specialists, manage delivery workflows, and keep every
              engagement moving from brief to outcome.
            </p>
          </section>
          <section className="grid gap-4 md:grid-cols-3">
            <div className="border-border rounded-2xl border bg-white p-5 shadow-sm">
              <p className="text-primary font-heading text-lg font-bold">Talent pipeline</p>
              <p className="text-muted mt-2 text-sm">
                Track curated freelancers by specialty, availability, and delivery readiness.
              </p>
            </div>
            <div className="border-border rounded-2xl border bg-white p-5 shadow-sm">
              <p className="text-primary font-heading text-lg font-bold">Assignment flow</p>
              <p className="text-muted mt-2 text-sm">
                Keep every brief, milestone, and handoff aligned across BOI project teams.
              </p>
            </div>
            <div className="border-border rounded-2xl border bg-white p-5 shadow-sm">
              <p className="text-primary font-heading text-lg font-bold">Workflow clarity</p>
              <p className="text-muted mt-2 text-sm">
                Surface approvals, blockers, and status updates in one shared operational view.
              </p>
            </div>
          </section>
          <p className="text-muted mt-2 text-sm">
            Backend status:{" "}
            <span className={backendStatus === "ok" ? "text-green-600" : "text-red-500"}>
              {backendStatus}
            </span>
          </p>
        </div>
      </main>
    </div>
  );
}
